import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ArrowLeft, Clock, MapPin, Star, IndianRupee, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const DriverTripHistory = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/api/driver/trips');
        setTrips(data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const completed = trips.filter((t) => t.status === 'completed');
  const totalEarnings = completed.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalTrips = completed.length;
  const ratingsList = completed.filter((t) => t.rating);
  const avgRating = ratingsList.length > 0
    ? (ratingsList.reduce((s, t) => s + t.rating, 0) / ratingsList.length).toFixed(1)
    : '0.0';

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Trip History</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Total Trips</p>
            <p className="text-3xl font-bold">{totalTrips}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Total Earnings</p>
            <p className="text-3xl font-bold text-green-600">₹{totalEarnings}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Avg Rating</p>
            <p className="text-3xl font-bold text-yellow-600">{avgRating} ⭐</p>
          </Card>
        </div>

        {loading ? (
          <Card className="p-12 text-center text-muted-foreground">Loading trips…</Card>
        ) : trips.length === 0 ? (
          <Card className="p-12 text-center">
            <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">No trips yet</h3>
            <p className="text-muted-foreground mb-6">Your completed trips will appear here</p>
            <Button onClick={() => navigate('/driver/dashboard')}>Go to Dashboard</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {trips.map((trip) => (
              <Card key={trip.id} className="p-6 hover:shadow-lg transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{trip.hours}hr Driver Booking</h3>
                    <p className="text-sm text-muted-foreground">Booking #{trip.id.slice(0, 8)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">₹{trip.amount}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      trip.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {trip.status}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Pickup</p>
                      <p className="text-foreground">{trip.pickup_location}</p>
                    </div>
                  </div>
                  {trip.drop_location && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Drop</p>
                        <p className="text-foreground">{trip.drop_location}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground/70" />
                      <span className="text-muted-foreground">
                        {format(new Date(trip.created_at), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground/70" />
                      <span className="text-muted-foreground">{trip.hours} Hour(s)</span>
                    </div>
                  </div>
                </div>

                {trip.rating && (
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={`star-${trip.id}-${star}`}
                            className={`w-4 h-4 ${
                              star <= trip.rating ? 'text-yellow-500 fill-current' : 'text-muted-foreground/50'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold">{trip.rating}/5</span>
                    </div>
                    {trip.review && <p className="text-sm text-muted-foreground italic">&ldquo;{trip.review}&rdquo;</p>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverTripHistory;
